import { createBrowserRouter, Navigate, useParams } from "react-router-dom";

import { AuthGate } from "@/auth/AuthGate";
import { Layout } from "@/components/Layout";
import { Account } from "@/pages/Account";
import { Login } from "@/pages/Login";
import { PhasesReference } from "@/pages/reference/Phases";
import { Signup } from "@/pages/Signup";
import { Teams } from "@/pages/Teams";
import { Workloads } from "@/pages/Workloads";

export const router = createBrowserRouter([
  // Public routes (no AuthGate). Login is the entry point for an
  // unauthenticated browser; /signup lets anyone apply for an account
  // (an existing admin must then approve it from the Teams page).
  // Both forms short-circuit to "/" if a session is already present.
  { path: "/login", element: <Login /> },
  { path: "/signup", element: <Signup /> },

  // /account is reachable for any logged-in user; AuthGate explicitly allows
  // this path even when must_change_password is true so the operator has a
  // way to satisfy the forced rotation.
  {
    path: "/account",
    element: (
      <AuthGate>
        <Account />
      </AuthGate>
    ),
  },

  // Everything else lives under the dashboard shell, gated by AuthGate.
  {
    path: "/",
    element: (
      <AuthGate>
        <Layout />
      </AuthGate>
    ),
    children: [
      { index: true, element: <Workloads /> },
      { path: "teams", element: <Teams /> },
      // Phases reference: catalogue of every pool / replica state
      // the reconciler can drive a workload into. Linked from every
      // PhaseBadge's ?-icon via `#pool-<slug>` / `#replica-<slug>`
      // anchors, and from the Help dropdown in the top bar.
      { path: "reference/phases", element: <PhasesReference /> },
      // Legacy routes — redirect into the unified Workloads view.
      { path: "services/new", element: <Navigate to="/" replace /> },
      { path: "services/:id/edit", element: <ServiceRedirect /> },
      { path: "dashboard", element: <Navigate to="/?tab=dashboard" replace /> },
      { path: "services", element: <Navigate to="/" replace /> },
      { path: "services/:id", element: <ServiceRedirect /> },
      { path: "replicas", element: <Navigate to="/" replace /> },
      { path: "cost", element: <Navigate to="/?tab=cost" replace /> },
      { path: "routing", element: <Navigate to="/?tab=dashboard" replace /> },
      { path: "schedules", element: <Navigate to="/?tab=dashboard" replace /> },
      { path: "events", element: <Navigate to="/?tab=events" replace /> },
      { path: "rollouts", element: <Navigate to="/?tab=rollouts" replace /> },
    ],
  },
]);

/**
 * /services/:id → /?focus=service:id&tab=dashboard
 *
 * Kept as a tiny component so we can read the URL param without forcing
 * the user to copy-paste the new query-string syntax.
 */
function ServiceRedirect() {
  const { id } = useParams();
  if (!id) return <Navigate to="/" replace />;
  return <Navigate to={`/?focus=service:${id}&tab=dashboard`} replace />;
}
